import React from "react";
import "./TableChart.scss";
import { Loader, Table } from "@SeemplicityDev/shared";
import type { ColumnDef } from "@tanstack/react-table";
import WidgetError from "../WidgetError/WidgetError";

// Generic row shape rendered by the underlying shared Table. Consumers build
// their own row objects (e.g. via tanstack createColumnHelper), so this is left
// open as a record; column definitions narrow access to specific fields.
export type TableChartRowData = Record<string, unknown>;

export interface TableChartProps {
  columns?: ColumnDef<TableChartRowData, unknown>[];
  className?: string;
  tableData?: TableChartRowData[] | null;
  isLoading?: boolean;
  error?: unknown;
  onRefetch?: () => void;
  legend?: React.ReactNode;
  noSelectedData?: boolean;
  selectorHeader?: React.ReactNode;
  isForExport?: boolean;
  noSelectedLoaderTitle?: string;
}

const TableChart: React.FC<TableChartProps> = ({
  columns,
  className,
  tableData,
  isLoading,
  error,
  onRefetch,
  legend,
  noSelectedData = false,
  selectorHeader = null,
  isForExport,
  noSelectedLoaderTitle,
}) => (
  <div
    className={`table-chart ${className ? className : ""} ${
      isForExport ? "exported" : ""
    }`}
  >
    {error ? (
      <WidgetError onWidgetsReload={onRefetch} />
    ) : !!tableData?.length || isLoading || noSelectedData ? (
      <>
        {!!selectorHeader && (
          <div className={"selection-header-section"}>{selectorHeader}</div>
        )}
        {!noSelectedData ? (
          <div
            className={`table-section ${
              !!selectorHeader ? "has-selector-header" : ""
            } ${!!legend ? "has-legend" : ""}`}
          >
            <Table
              columns={columns}
              data={tableData}
              noBorder
              loading={isLoading}
              loaderRowsAmount={5}
              rowHeight={56}
            />
            {!!legend && legend}
          </div>
        ) : (
          <Loader
            data-testid={"chart-no-data"}
            center
            smallIcon
            icon={"seem-icon-manualfinding"}
            subtitle={noSelectedLoaderTitle}
            className={"empty-state-no-match-to-filter no-selected-data"}
            noBackground
          />
        )}
      </>
    ) : (
      <Loader
        data-testid={"chart-no-data"}
        className={"empty-state-no-match-to-filter"}
        center
        icon={"seem-icon-manualfinding"}
        subtitle={"There is no match for the selected Scope / Filter"}
        noBackground
      />
    )}
  </div>
);

export default TableChart;
